// Client-side form abuse protection.
// Limits how many times a visitor can submit forms in a short window and
// neutralizes scriptable/control characters before data leaves the browser.
// This complements the honeypot field and never replaces server-side validation.

const STORAGE_KEY = "openlab-form-throttle";
const MAX_ATTEMPTS = 4;
const WINDOW_MS = 60 * 1000;
const MAX_FIELD_LENGTH = 500;

function readAttempts(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is number => typeof value === "number");
  } catch {
    return [];
  }
}

function writeAttempts(attempts: number[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  } catch {
    // storage unavailable (private mode) - fail open, still protected by honeypot
  }
}

/**
 * Returns true when the visitor has hit the submission limit inside the window.
 */
export function isSubmissionRateLimited(): boolean {
  const now = Date.now();
  const recent = readAttempts().filter((time) => now - time < WINDOW_MS);
  return recent.length >= MAX_ATTEMPTS;
}

/**
 * Records a successful submission attempt for the current window.
 */
export function registerSubmission(): void {
  const now = Date.now();
  const recent = readAttempts().filter((time) => now - time < WINDOW_MS);
  recent.push(now);
  writeAttempts(recent);
}

/**
 * Strips control/invisible characters and enforces a maximum length on a value.
 */
export function sanitizeFieldValue(value: string): string {
  const cleaned = Array.from(value)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return !(code <= 0x1f || code === 0x7f);
    })
    .join("");
  return cleaned.slice(0, MAX_FIELD_LENGTH).trim();
}